import React, { ReactNode, useMemo } from "react";
import styled from "styled-components";
import * as Router from "next/router";

import { Device } from "@/utils/device";
import * as u from "../../../utils";
import RxLink from "../inputs/RxLink";
import type { RoutesConfig } from "@/config/routes.config";

export type SubNavProps = {
  items: RoutesConfig[];
  includeQueryParams?: boolean;
  children?: ReactNode;
  paddingLeft?: string;
};

const RxSubNav: React.FC<SubNavProps> = ({
  children,
  ...props
}): JSX.Element => {
  const router = Router.useRouter();
  const items = useMemo(
    () =>
      props.items.map(({ path, text }) => {
        const isActive = props.includeQueryParams
          ? router.asPath === path
          : path === router.pathname;

        return (
          <S.RxLink
            href={path}
            key={path}
            data-cy={`subnav-tab-${text.replace(/ /g, "-").toLowerCase()}`}
            isActive={isActive}
          >
            {text}
          </S.RxLink>
        );
      }),
    [props.items]
  );

  if (!items.length) return null;

  return (
    <S.Container>
      <S.NavContainer paddingLeft={props.paddingLeft}>
        <div>{items}</div>
        <div>{children}</div>
      </S.NavContainer>
    </S.Container>
  );
};

interface StyleProps {
  isSecondary?: boolean;
  isActive?: boolean;
  paddingLeft?: string;
}

const S = () => {};

S.Container = styled.div`
  background: ${u.colours.buntingBlue};
  position: relative;
`;

S.NavContainer = styled.nav<StyleProps>`
  margin: 0 auto;
  color: white;
  max-width: 1440px;
  padding: 0 ${(p) => (p.paddingLeft ? p.paddingLeft : `10%`)} 10px;
  overflow-x: scroll;
  justify-content: space-between;
  scrollbar-width: none;
  -ms-overflow-style: none; /* IE and Edge */

  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: ${Device.tablet}) {
    display: flex;
    align-items: center;
    padding: 0 ${(p) => (p.paddingLeft ? p.paddingLeft : `10%`)};
  }
`;

S.RxLink = styled(RxLink)<StyleProps>`
  display: inline-block;
  padding: 14px 0;
  margin-right: 20px;
  position: relative;
  white-space: nowrap;
  text-decoration: none;
  font-size: 1.6rem;
  background-color: transparent;
  border: none;
  cursor: pointer;

  &:last-child {
    margin-right: 0;
  }

  ${(p) =>
    !p.isActive &&
    `
      &::after {
      background: none repeat scroll 0 0 transparent;
      content: "";
      display: block;
      height: 1px;
      position: absolute;
      background: white;
      transition: all 0.3s ease-out;
      width: 0;
    }

    &&:hover:after { 
      width: 100%; 
      left: 0; 
    }
  `}

  ${(p) =>
    p.isActive &&
    `
    font-weight: bold;
    text-decoration: underline;
    text-underline-offset: 4px;
  }`}
`;

export default RxSubNav;
